import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import {
  Component,
  OnInit,
  SecurityContext,
  DOCUMENT,
  inject,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ChangePasswordComponent } from '@generic/layout/change-password/change-password.component';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { AssetService } from '@services/asset.service';
import { AuthService } from '@services/auth.service';
import { CampaignsService } from '@services/campaigns.service';
import { ClientService } from '@services/client.service';
import { GameService } from '@services/game.service';
import { LabelsService } from '@services/labels.service';
import { SessionService } from '@services/session.service';
import { ThemeService } from '@services/theme.service';
import { AddClientDialogComponent } from '@shared/components/add-client-dialog/add-client-dialog.component';
import { BaseGame } from '@shared/types/base-game';
import { BaseTheme } from '@shared/types/base-theme';
import { Client } from '@shared/types/client';
import { GameType } from '@shared/types/game-type';
import { take, filter, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { CUSTOM_ICONS } from './custom-icons';
import { WheelSegmentsService } from './services/wheel-segments.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  readonly session = inject(SessionService);
  private readonly gameService = inject<GameService<BaseGame>>(GameService);
  private readonly authService = inject(AuthService);
  private readonly clientService = inject(ClientService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly labelService = inject(LabelsService);
  private readonly dialog = inject(MatDialog);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private themeService = inject<ThemeService<BaseTheme>>(ThemeService);
  private campaignsService = inject(CampaignsService);
  private wheelSegmentsService = inject(WheelSegmentsService);
  private assetService = inject(AssetService);
  private document = inject<Document>(DOCUMENT);

  isMobile = this.session.isMobile;
  client$ = this.session.client$;
  gameType$ = this.session.gameType$;
  clients$ = this.clientService.clients$;
  isLoading$ = this.gameService.isLoading$;
  authenticatedUser$ = this.session.authenticatedUser$;
  userRole$ = this.session.getUserRole();
  isSidenavOpen = this.session.isSidenavOpen;
  sidenavShouldBeOpened$ = combineLatest([
    this.session.client$,
    toObservable(this.isSidenavOpen),
    toObservable(this.isMobile),
  ]).pipe(
    map(([client, isSidenavOpen, isMobile]) => {
      if (!client) return false;
      if (isMobile) {
        return isSidenavOpen;
      }
      return true; // in this case the menu should be always opened
    }),
  );
  contentMargin$ = combineLatest([
    this.session.client$,
    toObservable(this.isSidenavOpen),
    toObservable(this.isMobile),
  ]).pipe(
    map(([client, isSidenavOpen, isMobile]) => {
      if (!client) return '0px';
      // if you want to change these values
      // then don't forget to adjust them in
      // app.component.css for sidenav content
      if (isMobile) {
        return '0px';
      }
      return isSidenavOpen ? '300px' : '72px';
    }),
  );

  constructor() {
    this.createIcons();
  }

  ngOnInit() {
    this.resetDataOnClientChange();
    this.checkUser();
    this.updateFavicon();
    this.authService.fetchUser();
    this.labelService.fetchSupportedLanguages().pipe(take(1)).subscribe();
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.session.isMobile.set(result.matches);
        this.session.checkSidenavMenuState();
      });
  }

  resetDataOnClientChange() {
    this.session
      .onSwitchClient()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.themeService.resetData();
        this.campaignsService.resetData();
        this.wheelSegmentsService.resetData();
        this.assetService.resetData();
      });
  }

  updateFavicon() {
    const favicon = this.document.getElementById('favicon') as HTMLLinkElement;
    const favicon32 = this.document.getElementById(
      'favicon32',
    ) as HTMLLinkElement;
    const favicon16 = this.document.getElementById(
      'favicon16',
    ) as HTMLLinkElement;
    this.session
      .onChangeClientLogo()
      .pipe(untilDestroyed(this))
      .subscribe((client) => {
        if (client.logoAsset?.url) {
          const url = this.sanitizer.sanitize(
            SecurityContext.URL,
            client.logoAsset.url,
          );
          if (url) {
            favicon.href = url;
            favicon32.href = url;
            favicon16.href = url;
          }
        } else {
          favicon.href = 'favicon.ico';
          favicon32.href = '/assets/favicon/favicon-32x32.png';
          favicon16.href = '/assets/favicon/favicon-16x16.png';
        }
      });
  }

  onSidenavClosed(): void {
    this.session.setSidenavOpen(false);
  }

  onAddClient(): void {
    this.dialog.open(AddClientDialogComponent, {
      minWidth: 500,
    });
  }

  onSetClient(client: Client): void {
    this.session.changeClient(client);
  }

  onSetGameType(gameType: GameType): void {
    this.session.onChangeGameType(gameType);
  }

  toggleSidenav(): void {
    this.session.changeSidenavMenuState();
  }

  logout() {
    this.authService.logout();
  }

  private createIcons(): void {
    CUSTOM_ICONS.forEach((icon) => {
      this.iconRegistry.addSvgIcon(
        icon.svg,
        this.sanitizer.bypassSecurityTrustResourceUrl(icon.path),
      );
    });
  }

  private checkUser(): void {
    this.authenticatedUser$
      .pipe(
        filter((user) => !!user),
        take(1),
        filter((user) => {
          return (
            !!user!.nextPasswordResetTs &&
            new Date(user!.nextPasswordResetTs).getTime() < new Date().getTime()
          );
        }),
      )
      .subscribe((user) => {
        this.dialog.open(ChangePasswordComponent, {
          hasBackdrop: true,
          disableClose: true,
          minWidth: 600,
          data: user,
        });
      });
  }
}
